<template>
  <v-dialog v-model="dialog" width="700px" scrollable persistent>
    <template v-slot:activator="{ on }">
      <v-text-field outlined hide-details :value="textFieldValue" v-on="on" />
    </template>

    <v-card>
      <v-card-title>Selecione um período</v-card-title>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row>
            <v-col>
              <v-select v-model="selectedPeriod" label="Opções" hide-details solo :items="selectOptions" @input="selectDates" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <mf-date-range-picker
                label="Data inicial - Data final"
                :initial-date.sync="initialDate"
                :final-date.sync="finalDate"
                :min="minDate"
                @input="setSelectedPeriodAsCustom"
              />
            </v-col>
          </v-row>
        </v-container>
        <mf-action-buttons
          :primary-button="{
            text: 'Confirmar',
            action: submit,
            isVisible: true,
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { DateTime } from 'luxon'

export default {
  name: 'DataAheadSelect',
  props: {
    value: [Array, String],
    defaultDate: {
      type: String,
      default: 'proximosSeteDias',
      validator: (value) =>
        ['diaMaisRecente', 'proximoDia', 'proximosSeteDias', 'proximosQuinzeDias', 'proximosTrintaDias', 'proximoMes'].indexOf(value) !== -1,
    },
  },
  data: () => ({
    dialog: false,
    selectedPeriod: '',
    initialDate: '',
    finalDate: '',
    now: DateTime.now(),
  }),
  computed: {
    textFieldValue() {
      switch (this.selectedPeriod) {
        case 'diaMaisRecente':
          return 'Dia mais recente'
        case 'proximoDia':
          return 'Próximo Dia'
        case 'proximosSeteDias':
          return 'Próximos 7 dias'
        case 'proximosQuinzeDias':
          return 'Próximos 15 dias'
        case 'proximosTrintaDias':
          return 'Próximos 30 dias'
        case 'proximoMes':
          return 'Próximo Mês'
        case 'periodoPersonalizado':
          const formattedInitialDate = DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd').toFormat('dd-MM-yyyy')
          const formattedFinalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd').toFormat('dd-MM-yyyy')
          return `${formattedInitialDate} - ${formattedFinalDate}`
        default:
          return ''
      }
    },

    selectOptions() {
      return [
        {
          text: 'Hoje',
          value: 'diaMaisRecente',
        },
        {
          text: 'Amanhã',
          value: 'proximoDia',
        },
        {
          text: 'Próximos 7 dias',
          value: 'proximosSeteDias',
        },
        {
          text: 'Próximos 15 dias',
          value: 'proximosQuinzeDias',
        },
        {
          text: 'Próximos 30 dias',
          value: 'proximosTrintaDias',
        },
        {
          text: 'Próximo mês',
          value: 'proximoMes',
        },
        {
          text: 'Período personalizado',
          value: 'periodoPersonalizado',
        },
      ]
    },

    today() {
      return this.now.toFormat('yyyy-MM-dd')
    },
    tomorrow() {
      return this.now.plus({ days: +1 }).toFormat('yyyy-MM-dd')
    },
    sevenDaysAhead() {
      return this.now.plus({ days: +7 }).toFormat('yyyy-MM-dd')
    },
    fifteenDaysAhead() {
      return this.now.plus({ days: +15 }).toFormat('yyyy-MM-dd')
    },
    thirtyDaysAhead() {
      return this.now.plus({ days: +30 }).toFormat('yyyy-MM-dd')
    },
    firstDayOfCurrentMonth() {
      return this.now.startOf('month').toFormat('yyyy-MM-dd')
    },
    lastDayOfCurrentMonth() {
      return this.now.endOf('month').toFormat('yyyy-MM-dd')
    },
    firstDayOfNextMonth() {
      return this.now.plus({ months: +1 }).startOf('month').toFormat('yyyy-MM-dd')
    },
    lastDayOfNextMonth() {
      return this.now.plus({ month: +1 }).endOf('month').toFormat('yyyy-MM-dd')
    },
    selectedOption: {
      get() {
        return this.selected
      },
      set(value) {
        this.$emit('update:selected', value)
      },
    },
    minDate() {
      return DateTime.now().toFormat('yyyy-MM-dd')
    },
  },
  watch: {
    dialog(value) {
      if (value) {
        if (!this.value) {
          this.selectDates(this.defaultDate)
        }
      }
    },
    value(value) {
      if (!value) {
        this.selectedPeriod = this.defaultDate
      }
    },
    initialDate(value) {
      if (value) {
        const formattedInitialDate = DateTime.fromFormat(value[0], 'yyyy-MM-dd')
        const formattedFinalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd')
        if (formattedInitialDate > formattedFinalDate) {
          this.finalDate = formattedInitialDate.toFormat('yyyy-MM-dd')
        }
      }
    },
    finalDate(value) {
      if (value) {
        const formattedFinalDate = DateTime.fromFormat(value[1], 'yyyy-MM-dd')
        const formattedInitialDate = this.initialDate ? DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd') : DateTime.now().startOf('year')
        if (formattedFinalDate < formattedInitialDate) {
          this.initialDate = formattedFinalDate.toFormat('yyyy-MM-dd')
        }
      }
    },
  },
  async mounted() {
    this.selectedPeriod = this.defaultDate
    this.selectDates(this.defaultDate)
    // Caso o filtro tenha uma data inicial diferente do padrão, é necessário que o componente
    // filtre com a data correta, antes de tudo.
    // Isso não estava acontecendo, porque o menu só é criado quando é aberto
    // Por causa disso será necessário 'montar' o componente via código,
    // para que a data seja filtrada corretamente.
  },
  methods: {
    submit() {
      this.$emit('input', [this.initialDate, this.finalDate])
      this.dialog = false
    },
    selectDates(value) {
      switch (value) {
        case 'diaMaisRecente':
          this.setInitialAndFinalDate({ initial: this.today, final: this.today })
          break
        case 'proximoDia':
          this.setInitialAndFinalDate({ initial: this.tomorrow, final: this.tomorrow })
          break
        case 'proximosSeteDias':
          this.setInitialAndFinalDate({ initial: this.today, final: this.sevenDaysAhead })
          break
        case 'proximosQuinzeDias':
          this.setInitialAndFinalDate({ initial: this.today, final: this.fifteenDaysAhead })
          break
        case 'proximosTrintaDias':
          this.setInitialAndFinalDate({ initial: this.today, final: this.thirtyDaysAhead })
          break
        case 'proximoMes':
          this.setInitialAndFinalDate({ initial: this.firstDayOfNextMonth, final: this.lastDayOfNextMonth })
          break
        case 'periodoPersonalizado':
          this.setInitialAndFinalDate({ initial: this.today, final: this.today })
          break
        default:
          break
      }
    },
    setInitialAndFinalDate({ initial, final }) {
      this.initialDate = initial
      this.finalDate = final
    },
    setSelectedPeriodAsCustom() {
      this.selectedPeriod = 'periodoPersonalizado'
    },
  },
}
</script>
<style lang="scss">
.data-select {
  .v-text-field__details {
    margin: 0 !important;
  }
}

.date-card {
  user-select: none;
  cursor: pointer;
}
</style>
